import { CAPABILITY_KEYS, type CapabilityKey } from "../utils/capabilityKeys";
import { canUseAccountCalendar, hasCapability } from "../utils/capabilities";
import { getTutorialDemoAccountPath } from "./tutorialDemoData";
import type { TutorialStep, TutorialStepId } from "./tutorialSteps";

/**
 * Capabilities required for a tutorial step (all must be enabled).
 * Steps omitted here are not capability-gated (role filter still applies).
 */
export const TUTORIAL_STEP_REQUIRED_CAPABILITIES: Partial<
  Record<TutorialStepId, readonly CapabilityKey[]>
> = {
  // Hotlist
  "nav-hotlist": [CAPABILITY_KEYS.HOTLIST],
  "hotlist-actions": [CAPABILITY_KEYS.HOTLIST],
  "team-rep-filter": [CAPABILITY_KEYS.HOTLIST],
  "hotlist-schedule": [CAPABILITY_KEYS.HOTLIST],
  "hotlist-view-switch": [CAPABILITY_KEYS.HOTLIST],
  "hotlist-work-action": [CAPABILITY_KEYS.HOTLIST],
  "hotlist-column-picker": [CAPABILITY_KEYS.HOTLIST],
  "hotlist-table-rows": [CAPABILITY_KEYS.HOTLIST],
  "hotlist-action-context": [CAPABILITY_KEYS.HOTLIST],
  "hotlist-action-prompt": [CAPABILITY_KEYS.HOTLIST],
  "hotlist-action-footer": [CAPABILITY_KEYS.HOTLIST],

  // Accounts portfolio / workspace shell
  "nav-accounts": [CAPABILITY_KEYS.ACCOUNTS],
  "accounts-portfolio": [CAPABILITY_KEYS.ACCOUNTS],
  "accounts-search": [CAPABILITY_KEYS.ACCOUNTS],
  "accounts-add": [CAPABILITY_KEYS.ACCOUNTS],
  "accounts-open": [CAPABILITY_KEYS.ACCOUNTS],
  "accounts-bulk-assign": [CAPABILITY_KEYS.ACCOUNTS],
  "accounts-row-assign": [CAPABILITY_KEYS.ACCOUNTS],
  "accounts-enter": [CAPABILITY_KEYS.ACCOUNTS],
  "account-masthead-meta": [CAPABILITY_KEYS.ACCOUNTS],

  // Account narrative radar (list expand)
  "accounts-row-expand": [CAPABILITY_KEYS.ACCOUNT_NARRATIVE_RADAR],

  // Account calendar (hotlist ∧ accounts)
  "account-calendar-tab": [CAPABILITY_KEYS.HOTLIST, CAPABILITY_KEYS.ACCOUNTS],
  "account-calendar-action": [CAPABILITY_KEYS.HOTLIST, CAPABILITY_KEYS.ACCOUNTS],
  "account-calendar-view-toggle": [CAPABILITY_KEYS.HOTLIST, CAPABILITY_KEYS.ACCOUNTS],

  // Account signals / insights
  "account-insights-tab": [CAPABILITY_KEYS.ACCOUNT_SIGNALS],
  "account-signals-overview": [CAPABILITY_KEYS.ACCOUNT_SIGNALS],
  "account-signals-actions": [CAPABILITY_KEYS.ACCOUNT_SIGNALS],
  "account-insights-view-toggle": [CAPABILITY_KEYS.ACCOUNT_SIGNALS],
  "account-insights-table": [CAPABILITY_KEYS.ACCOUNT_SIGNALS],
  "account-insights-row-expand": [CAPABILITY_KEYS.ACCOUNT_SIGNALS],
  "account-insights-table-actions": [CAPABILITY_KEYS.ACCOUNT_SIGNALS],

  // Account contacts
  "account-contacts-tab": [CAPABILITY_KEYS.ACCOUNTS],
  "account-contacts": [CAPABILITY_KEYS.ACCOUNTS],
  "account-contacts-row-expand": [CAPABILITY_KEYS.ACCOUNTS],
  "account-contacts-actions": [CAPABILITY_KEYS.ACCOUNTS],
  "account-contacts-reorder": [CAPABILITY_KEYS.ACCOUNTS],
  "account-contacts-social": [CAPABILITY_KEYS.ACCOUNTS],

  // Outreach workspace demo (hosted on account insights table)
  "outreach-icons": [CAPABILITY_KEYS.ACCOUNT_SIGNALS],
  "social-prompt-sequence": [CAPABILITY_KEYS.ACCOUNT_SIGNALS, CAPABILITY_KEYS.CADENCES],
  "social-prompt-compose": [CAPABILITY_KEYS.ACCOUNT_SIGNALS],
  "social-prompt-contacts": [CAPABILITY_KEYS.ACCOUNT_SIGNALS],
  "social-prompt-footer": [CAPABILITY_KEYS.ACCOUNT_SIGNALS],
  "social-prompt-request-more": [CAPABILITY_KEYS.ACCOUNT_SIGNALS],
  "social-prompt-request-setup": [CAPABILITY_KEYS.ACCOUNT_SIGNALS],
  "social-prompt-request-cadence": [CAPABILITY_KEYS.ACCOUNT_SIGNALS, CAPABILITY_KEYS.CADENCES],
  "social-prompt-request-recipient": [CAPABILITY_KEYS.ACCOUNT_SIGNALS],
  "social-prompt-request": [CAPABILITY_KEYS.ACCOUNT_SIGNALS],
  "insight-complete-icon": [CAPABILITY_KEYS.ACCOUNT_SIGNALS],
  "insight-complete": [CAPABILITY_KEYS.ACCOUNT_SIGNALS],
  "insight-dismiss-icon": [CAPABILITY_KEYS.ACCOUNT_SIGNALS],
  "insight-dismiss": [CAPABILITY_KEYS.ACCOUNT_SIGNALS],

  // Industry insights
  "insights-overview": [CAPABILITY_KEYS.INDUSTRIES],
  "insights-library": [CAPABILITY_KEYS.INDUSTRIES],
  "insights-competitors": [CAPABILITY_KEYS.INDUSTRIES],
  "nav-insights": [CAPABILITY_KEYS.INDUSTRIES],

  // Assets
  "nav-assets": [CAPABILITY_KEYS.ASSETS],
  "assets-library": [CAPABILITY_KEYS.ASSETS],
  "assets-row-expand": [CAPABILITY_KEYS.ASSETS],
  "assets-social-prompt": [CAPABILITY_KEYS.ASSETS],
  "assets-add": [CAPABILITY_KEYS.ASSETS],

  // Settings — cadence templates only when cadences are on
  "settings-cadence-templates": [CAPABILITY_KEYS.CADENCES],
};

export function tutorialStepMatchesCapabilities(
  step: TutorialStep,
  capabilities: Record<string, boolean> | null | undefined,
  options?: { ungated?: boolean }
): boolean {
  if (options?.ungated) return true;
  if (!capabilities) return true;

  const required = TUTORIAL_STEP_REQUIRED_CAPABILITIES[step.id];
  if (!required?.length) return true;
  return required.every((key) => hasCapability(capabilities, key));
}

/** Prefer calendar landing when available; otherwise open insights. */
export function adaptTutorialStepsForCapabilities(
  steps: TutorialStep[],
  capabilities: Record<string, boolean> | null | undefined,
  options?: { ungated?: boolean }
): TutorialStep[] {
  if (options?.ungated || !capabilities) return steps;

  const calendarOk = canUseAccountCalendar(capabilities);
  if (calendarOk) return steps;

  const insightsRoute = getTutorialDemoAccountPath("insights");
  return steps.map((step) => {
    if (step.id !== "accounts-enter" && step.id !== "account-masthead-meta") {
      return step;
    }
    if (!step.route || step.route === insightsRoute) return step;
    return {
      ...step,
      route: insightsRoute,
      ...(step.id === "accounts-enter"
        ? {
            body:
              "We're opening a sample account workspace. Name, domain, and view switches sit up top — use the tabs to move between insights and contacts.",
            bullets: [
              "Sample data is labeled — your workspace may still be empty.",
              "Use the back button or Accounts nav to return to the portfolio.",
              "Next we’ll look at company info and tags.",
            ],
          }
        : {}),
    };
  });
}

export function filterTutorialStepsByCapabilities(
  steps: TutorialStep[],
  capabilities: Record<string, boolean> | null | undefined,
  options?: { ungated?: boolean }
): TutorialStep[] {
  const filtered = steps.filter((step) =>
    tutorialStepMatchesCapabilities(step, capabilities, options)
  );
  return adaptTutorialStepsForCapabilities(filtered, capabilities, options);
}
